import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import debounce from 'lodash-es/debounce'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { STRIPE_CLIENT_PORTAL, WEBSITE_URL } from '../../../env'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import JoinUs from '../../../sharedComponents/src/Dialogs/Forms/JoinUs'
import { MiniProfile } from '../../../sharedComponents/src/globalHeader/components/MiniProfile'
import {
    changePassword,
    editProfile,
    loadUserProfileData,
    updateSessionUser,
    uploadAvatar,
} from '../../../sharedComponents/src/globalHeader/functions/ams-functions'
import { Rules, UserData } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { getAppConfig, IAppConfig } from '../../../sharedComponents/src/globalHeader/services/AppConfigService'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import CountryDropdown, { CountryItem } from '../Inputs/CountryDropdown'
import FileUpload, { FILE_FORMAT, MAX_FILE_SIZE, MB_SIZE } from '../Inputs/FileUpload'
import Input from '../Inputs/Input'
import { validatePassword } from '../../../sharedComponents/src/utils/passwordValidation'

const ProfileAccount: React.FC = () => {
    const user = useStoreState((state) => state.userData.userData)
    const newAvatar = useStoreState((state) => state.profileView.newAvatar)
    const currentAvatar = useStoreState((state) => state.profileView.currentAvatar)
    const rules = useStoreState((state) => state.rules)

    const updateUserData = useStoreActions((state) => state.updateUserData)
    const setNewAvatar = useStoreActions((state) => state.profileView.setNewAvatar)
    const setCurrentAvatar = useStoreActions((state) => state.profileView.setCurrentAvatar)
    const setSnackbarMessage = useStoreActions((state) => state.setSnackbarMessage)

    const [userData, setUserData] = useState(user || undefined)
    const [avatarError, setAvatarError] = useState<string>('')
    const [appConfig, setAppConfig] = useState<IAppConfig | null>(null)
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [isNewPasswordValid, setIsNewPasswordValid] = useState<boolean | undefined>(undefined)
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('')
    const { registerDialog } = useDialogs()

    const settingsEnabled = rules.some((rule) => rule === Rules.FULL_ACCESS || rule === Rules.GAME_SETTINGS)

    useEffect(() => {
        const config = getAppConfig()
        setAppConfig(config)
    }, [])

    useEffect(() => {
        if (user && user.id !== userData?.id) {
            setUserData(user)
            setNewAvatar(null)
        }
    }, [user])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, type, checked, value } = e.target
        const newValue = type === 'checkbox' || type === 'switch' ? checked : value
        setUserData((prevUserData: any) => ({
            ...prevUserData,
            [name]: newValue,
        }))
    }

    const countryChangeHandler = (selectedCountry: CountryItem) => {
        setUserData((prevUserData: any) => {
            return {
                ...prevUserData,
                country: selectedCountry.code,
            }
        })
    }

    const userDataChangeHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (userData) {
            if (newAvatar) {
                const avatarResult = await uploadAvatar(newAvatar)
                setNewAvatar(null)
                if (avatarResult !== true) {
                    setAvatarError(avatarResult.error.message)
                    return
                }
            }
            const result = await editProfile(userData)
            if (result.error) {
                setSnackbarMessage(result.error.message)
            } else {
                const data: UserData = await loadUserProfileData()
                updateUserData(data)
                setCurrentAvatar(data?.avatar_url || '')
                updateSessionUser(data)
                setSnackbarMessage(result.message)
            }
        }
    }

    const onNewPasswordChange = (password: string) => {
        const result = validatePassword(password)
        setIsNewPasswordValid(result.isValid)
        setPasswordErrorMessage(result.errorMessage)
    }

    const debouncedNewPasswordChange = useCallback(debounce(onNewPasswordChange, 500), [])

    useEffect(() => {
        if (newPassword === '') {
            setIsNewPasswordValid(undefined)
            setPasswordErrorMessage('')
            return
        }

        const timer = setTimeout(() => {
            debouncedNewPasswordChange(newPassword)
        }, 500)

        return () => {
            clearTimeout(timer)
        }
    }, [newPassword, debouncedNewPasswordChange])

    const changePasswordHandler = async (e: any) => {
        e.preventDefault()

        if (!isNewPasswordValid) return

        const data = await changePassword(currentPassword, newPassword)
        if (data.message) {
            setSnackbarMessage(data.message)
            return
        } else if (data.error) {
            setSnackbarMessage(data.error.message)
            return
        }

        setSnackbarMessage('Password changed successfully!')
        sessionStorage.setItem('_icc_user', '')
        setCurrentPassword('')
        setNewPassword('')
        setIsNewPasswordValid(undefined)
    }

    const onUploadAvatar = async (file: File) => {
        if (settingsEnabled) {
            if (!(file.type === FILE_FORMAT.JPEG || file.type === FILE_FORMAT.PNG)) {
                setAvatarError('JPEG or PNG File Required')
            } else if (file.size > MAX_FILE_SIZE * MB_SIZE) {
                setAvatarError(`File exceeds ${MAX_FILE_SIZE}MB`)
            } else {
                setNewAvatar(file)
                if (user) {
                    updateUserData({ ...user, avatar_url: URL.createObjectURL(file) })
                }
                setAvatarError('')
            }
        } else {
            setSnackbarMessage('You do not have permission to change your avatar.')
        }
    }

    const onAvatarUploadCancel = async () => {
        if (user && currentAvatar) {
            updateUserData({ ...user, avatar_url: currentAvatar })
        }
        setNewAvatar(null)
        setAvatarError('')
    }

    const onAvatarDisableClick = () => {
        registerDialog(Dialogs.JOIN_US, <JoinUs />)
    }

    const getUserStatus = (id: number) => {
        switch (id) {
            case 0:
                return 'Member: Free'
            case 1:
                return 'Member: Premium'
            default:
                return 'Unknown status'
        }
    }

    return userData ? (
        <Stack
            sx={{
                px: 3,
                button: {
                    maxWidth: '300px',
                },
            }}
        >
            <Stack sx={{ borderBottom: '2px solid', borderBottomColor: 'text.primary', pb: 1 }}>
                <Typography variant="h3" textTransform="uppercase">
                    Account
                </Typography>
            </Stack>
            <Stack
                sx={{
                    '> div': {
                        flexDirection: 'row-reverse',
                        justifyContent: 'flex-end',
                        gap: 2,
                        pt: 2,
                        pb: 0,
                        px: 0,
                    },
                }}
            >
                <MiniProfile userData={userData} />
            </Stack>
            <form onSubmit={userDataChangeHandler}>
                <Stack spacing={2} sx={{ pt: 1.5, pb: 5, mb: 5 }}>
                    <Input
                        label="Username"
                        readOnly={true}
                        type="text"
                        name="user_name"
                        value={userData.user_name || ''}
                        onChange={onChange}
                    />
                    <Input
                        label="First Name"
                        type="text"
                        name="first_name"
                        value={userData.first_name || ''}
                        onChange={onChange}
                    />
                    <Input
                        label="Last Name"
                        type="text"
                        name="last_name"
                        value={userData.last_name || ''}
                        onChange={onChange}
                    />
                    <Input
                        label="E-mail Address"
                        readOnly={true}
                        type="email"
                        name="email"
                        value={userData?.email || ''}
                        onChange={onChange}
                    />
                    <CountryDropdown
                        label="Country"
                        value={userData?.country || ''}
                        onCountryChange={countryChangeHandler}
                    />
                    <Input
                        label="Receive Email Notifications"
                        type="switch"
                        name="subscribed_to_mailing_list"
                        value={userData.subscribed_to_mailing_list || false}
                        onChange={onChange}
                    />
                    <FileUpload
                        value={newAvatar}
                        text="upload avatar"
                        error={avatarError}
                        onChange={onUploadAvatar}
                        onCancel={onAvatarUploadCancel}
                        onDisableClick={onAvatarDisableClick}
                        disabled={!settingsEnabled}
                    />
                    <Button disabled={Boolean(avatarError)} type="submit">
                        SAVE
                    </Button>
                </Stack>
            </form>
            <Stack sx={{ borderBottom: '2px solid', borderBottomColor: 'text.primary', pb: 1 }}>
                <Typography variant="h5">CHANGE PASSWORD</Typography>
            </Stack>
            <form onSubmit={changePasswordHandler}>
                <Stack spacing={2} sx={{ pt: 1.5, pb: 5, mb: 5 }}>
                    <Input
                        label="Current Password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                    <Input
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        error={isNewPasswordValid === false}
                        helperText={isNewPasswordValid === false ? passwordErrorMessage : undefined}
                    />
                    <Button type="submit" disabled={!isNewPasswordValid || !currentPassword}>
                        CHANGE
                    </Button>
                </Stack>
            </form>

            <Stack sx={{ borderBottom: '2px solid', borderBottomColor: 'text.primary', pb: 1 }}>
                <Typography variant="h5">SUBSCRIPTION INFORMATION</Typography>
            </Stack>
            <Stack spacing={4} sx={{ pt: 3, pb: 10 }}>
                <Grid container>
                    <Grid item xs={4} sx={{ px: 1.5 }}>
                        <Typography variant="body2" textTransform="uppercase">
                            Subscriber
                        </Typography>
                    </Grid>
                    <Grid item xs={8} sx={{ px: 1.5 }}>
                        <Typography variant="body2">{getUserStatus(userData.subscription_state)}</Typography>
                    </Grid>
                </Grid>
                {/* <Grid container>
                    <Grid item xs={4} sx={{ px: 1.5 }}>
                        <Typography variant="body2" textTransform="uppercase">
                            Expiration Date
                        </Typography>
                    </Grid>
                    <Grid item xs={8} sx={{ px: 1.5 }}>
                        <Typography variant="body2">{formatExpiry(userData.subscription_days_left)}</Typography>
                    </Grid>
                </Grid> */}
                <Stack spacing={2}>
                    <Link to={`${STRIPE_CLIENT_PORTAL}`}>
                        <Button fullWidth>MANAGE MY SUBSCRIPTION</Button>
                    </Link>
                    <Link to={`${WEBSITE_URL}/memberships`}>
                        <Button fullWidth>PURCHASE SUBSCRIPTION</Button>
                    </Link>
                </Stack>
            </Stack>

            {appConfig?.hideAccountDeletion === false ? (
                <>
                    <Stack sx={{ borderBottom: '2px solid', borderBottomColor: 'text.primary', pb: 1 }}>
                        <Typography variant="h5">Account deletion</Typography>
                    </Stack>
                    <Stack sx={{ pt: 1.5, pb: 5, my: 2 }}>
                        <Link to={`${WEBSITE_URL}/account-deletion`}>
                            <Button fullWidth variant="contained" color="error">
                                Delete my account
                            </Button>
                        </Link>
                    </Stack>
                </>
            ) : null}
        </Stack>
    ) : (
        <Stack>Loading...</Stack>
    )
}

ProfileAccount.displayName = 'ProfileAccount'

export default ProfileAccount
